import { SelectedTab } from 'features/navbar/types'
import { useGeckoPermissions } from 'features/permissions'
import type { WorkspaceTabAccessPolicy } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { useMemo } from 'react'
import {
  PERMISSION_CANTILEVER_POWER_FIM_VIEWER,
  PERMISSION_GECKO_INTERNAL_MEMBER,
} from 'types'

/**
 * The single access policy for Facility Model and Model Operations. Used by
 * the navbar, the embedded sidebar, route guards, resource-tab opening, and
 * restored-tab validation so a direct URL or a restored tab can never bypass
 * what the navbar enforces. Both surfaces require the `cantilever:power-fim:viewer`
 * tier — `canView` (read-only Facility Model access) needs only that;
 * `canOperate`/`canViewModelOperations` (Model Operations) additionally
 * requires gecko-internal.
 */
export interface FacilityModelAccess {
  isLoading: boolean
  canView: boolean
  canOperate: boolean
  canViewModelOperations: boolean
}

export const useFacilityModelAccess = (
  siteId: string | undefined
): FacilityModelAccess => {
  const { permissions, hasPermissions } = useGeckoPermissions()

  return useMemo(() => {
    const isLoading = permissions === undefined
    const isInternal =
      !isLoading && hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])
    const hasFimViewer =
      !isLoading && hasPermissions([PERMISSION_CANTILEVER_POWER_FIM_VIEWER])
    const canView = hasFimViewer && siteId !== undefined
    const canOperate = isInternal && hasFimViewer && siteId !== undefined
    return {
      isLoading,
      canView,
      canOperate,
      canViewModelOperations: canOperate,
    }
  }, [hasPermissions, permissions, siteId])
}

const GATED_RESOURCE_KEY_PREFIXES = ['subject:'] as const
const GATED_DESTINATION_KEYS: ReadonlySet<string> = new Set([
  SelectedTab.FacilityModel,
  SelectedTab.ModelOperations,
])

/**
 * Restored-tab validator for the workspace provider. Only tabs belonging to
 * the Facility Model / Model Operations surface are validated; every other
 * tab keeps its existing behavior. `isLoading` stays true until permissions
 * resolve so a slow fetch can never delete valid tabs.
 */
export const useWorkspaceTabAccessPolicy = (
  siteId: string | undefined
): WorkspaceTabAccessPolicy => {
  const access = useFacilityModelAccess(siteId)

  return useMemo(
    () => ({
      isLoading: access.isLoading,
      canOpenTab: (tab) => {
        const key = tab.resourceKey
        const isGated =
          (key !== undefined &&
            (GATED_RESOURCE_KEY_PREFIXES.some((prefix) =>
              key.startsWith(prefix)
            ) ||
              (key.startsWith('destination:') &&
                GATED_DESTINATION_KEYS.has(
                  key.slice('destination:'.length)
                )))) ||
          (tab.workspaceDestination !== undefined &&
            GATED_DESTINATION_KEYS.has(tab.workspaceDestination))
        if (!isGated) return true
        const needsModelOperations =
          key === `destination:${SelectedTab.ModelOperations}` ||
          tab.workspaceDestination === SelectedTab.ModelOperations
        return needsModelOperations
          ? access.canViewModelOperations
          : access.canView
      },
    }),
    [access]
  )
}
